import { useEffect, useState } from 'react';
import { useSEOSettings } from '@/hooks/useSEOSettings';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { FEATURE_OPTIONS } from '@/constants/planFeatures';
import {
  Package, Zap, Download, Settings, ShoppingCart, FileText, Sparkles,
  Check, ArrowRight, Star, Shield, Globe, Loader2, ChevronRight, Layers,
  Search, Bot, AlertTriangle, X, FileDown, Upload, Play, Rocket,
  TrendingUp, Clock, Users, BarChart3,
} from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  price_usd: number;
  interval_days: number;
  features: string[] | null;
  is_active: boolean;
}

const featureLabelMap: Record<string, string> = {};
FEATURE_OPTIONS.forEach(f => { featureLabelMap[f.key] = f.label; });

const TIER_DETAILS: Record<string, { color: string; gradient: string; icon: typeof Package; tagline: string; detailedFeatures: string[] }> = {
  basic: {
    color: 'from-blue-500 to-cyan-500',
    gradient: 'from-blue-500/10 to-cyan-500/10',
    icon: Package,
    tagline: 'Perfect for getting started',
    detailedFeatures: [
      'Scrape Shopify products by URL',
      'Export products to CSV',
      'Bulk edit prices, tags & vendors',
      'Product detail viewer & editor',
      'Out-of-stock visual indicators',
      'Unlimited products in workspace',
    ],
  },
  standard: {
    color: 'from-violet-500 to-purple-500',
    gradient: 'from-violet-500/10 to-purple-500/10',
    icon: Zap,
    tagline: 'Best for growing stores',
    detailedFeatures: [
      'Everything in Basic, plus:',
      'Scrape WooCommerce products',
      'AI Product Fixer — titles, SEO, availability',
      'Push products to Shopify & WooCommerce',
      'Import & transform CSV files',
      'Fix products directly from dashboard',
      'Smart inventory auto-fix',
    ],
  },
  pro: {
    color: 'from-amber-500 to-orange-500',
    gradient: 'from-amber-500/10 to-orange-500/10',
    icon: Shield,
    tagline: 'Full power for professionals',
    detailedFeatures: [
      'Everything in Standard, plus:',
      'GMC Compliance Scanner (30+ checks)',
      'AI GMC Fixer — fix & download CSV',
      'AI Privacy Policy Fixer (Privacy, Terms, Refund...)',
      'Multi-AI provider support — BYO key (free or paid)',
      'Downloadable PDF compliance reports',
      'Misrepresentation & policy scan',
      'Website quality & product data audit',
      'Priority support',
    ],
  },
};

function getTierKey(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('pro')) return 'pro';
  if (n.includes('standard')) return 'standard';
  return 'basic';
}

const Home = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const { settings } = useSiteSettings();
  const { getPageSEO, settings: seoSettings } = useSEOSettings();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_usd', { ascending: true });
      if (!error && data) {
        setPlans(data.map(p => ({
          ...p,
          features: Array.isArray(p.features) ? p.features as string[] : [],
        })));
      }
    } catch {
      // silently fail
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleGetStarted = () => {
    navigate(isAuthenticated ? '/dashboard' : '/auth');
  };

  const features = [
    { icon: Package, title: 'Shopify Scraping', description: 'Scrape any Shopify collection — products, variants, images, and metadata extracted instantly.', color: 'from-blue-500/15 to-cyan-500/15' },
    { icon: Globe, title: 'WooCommerce Scraping', description: 'Scrape WooCommerce stores via public URLs or authenticated REST API with category support.', color: 'from-emerald-500/15 to-teal-500/15' },
    { icon: Bot, title: 'AI Product Fixer', description: 'Fix titles, descriptions, SEO handles, tags, availability, and inventory with AI in one click.', color: 'from-violet-500/15 to-purple-500/15' },
    { icon: ShoppingCart, title: 'Push to Store', description: 'Push products directly to Shopify or WooCommerce with full variant and image support.', color: 'from-orange-500/15 to-amber-500/15' },
    { icon: Search, title: 'GMC Scanner', description: 'Scan for Google Merchant Center violations — misrepresentation, policy, quality & product data.', color: 'from-rose-500/15 to-pink-500/15' },
    { icon: Settings, title: 'Bulk Operations', description: 'Bulk price changes, vendor updates, tag edits, and image management across hundreds of products.', color: 'from-sky-500/15 to-blue-500/15' },
    { icon: Download, title: 'Multi-Format Export', description: 'Export to Shopify CSV, WooCommerce CSV, or Google Merchant XML with one click.', color: 'from-teal-500/15 to-emerald-500/15' },
    { icon: FileText, title: 'CSV Import & Transform', description: 'Import products from CSV and transform between Shopify, WooCommerce, and custom formats.', color: 'from-indigo-500/15 to-violet-500/15' },
    { icon: Shield, title: 'PDF Reports', description: 'Download GMC compliance reports as PDF. Fix and re-download product CSV before uploading.', color: 'from-amber-500/15 to-yellow-500/15' },
    { icon: FileText, title: 'AI Policy Fixer', description: 'Rewrite Privacy, Terms, Refund & Shipping policies into Google Merchant Center–compliant HTML using AI.', color: 'from-fuchsia-500/15 to-pink-500/15' },
    { icon: Bot, title: 'Multi-AI Provider — BYO Key', description: 'Bring your own API key from Gemini, Groq, OpenRouter, OpenAI or Anthropic. Use free tiers or paid plans, with automatic fallback if one fails.', color: 'from-cyan-500/15 to-blue-500/15' },
  ];

  const stats = [
    { value: '10K+', label: 'Products Managed', icon: Layers },
    { value: '500+', label: 'Active Merchants', icon: Users },
    { value: '30+', label: 'GMC Checks', icon: Search },
    { value: '99.9%', label: 'Uptime', icon: TrendingUp },
  ];

  const gmcCategories = [
    { icon: AlertTriangle, label: 'Misrepresentation', desc: 'Detect misleading claims & missing business info', color: 'bg-rose-500/10 text-rose-500' },
    { icon: FileText, label: 'Policy Violations', desc: 'Check privacy, refund & terms compliance', color: 'bg-amber-500/10 text-amber-500' },
    { icon: Globe, label: 'Website Quality', desc: 'Audit navigation, SSL, mobile & UX', color: 'bg-blue-500/10 text-blue-500' },
    { icon: ShoppingCart, label: 'Product Data', desc: 'Validate titles, images, pricing & availability', color: 'bg-emerald-500/10 text-emerald-500' },
  ];

  const formatInterval = (days: number) => {
    if (days === 30 || days === 31) return '/mo';
    if (days === 365 || days === 366) return '/yr';
    if (days === 7) return '/wk';
    return `/${days}d`;
  };

  const policyLinks = [
    { label: 'Privacy Policy', path: '/policy/privacy-policy' },
    { label: 'Terms of Service', path: '/policy/terms-of-service' },
    { label: 'Refund Policy', path: '/policy/refund-policy' },
    { label: 'Shipping Policy', path: '/policy/shipping-policy' },
  ];

  return (
    <>
      <SEO
        title={getPageSEO('home').title || `${settings.site_name} — Scrape, Fix & Push E-commerce Products`}
        description={getPageSEO('home').description || "Scrape, edit, fix and export products from any Shopify or WooCommerce store. AI-powered product management and GMC compliance."}
        canonical="/"
        keywords={[seoSettings.global_keywords, getPageSEO('home').keywords].filter(Boolean).join(', ') || undefined}
        image={getPageSEO('home').og_image || seoSettings.default_og_image || undefined}
      />
      <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
        {/* Header */}
        <header className="border-b bg-background/60 backdrop-blur-2xl sticky top-0 z-50">
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
                {settings.site_logo_url ? (
                  <img src={settings.site_logo_url} alt={settings.site_name} className="h-8 sm:h-9 w-auto max-w-[140px] object-contain" />
                ) : (
                  <div className="p-2 gradient-primary rounded-xl shadow-glow">
                    <Rocket className="h-5 w-5 sm:h-6 sm:w-6 text-primary-foreground" />
                  </div>
                )}
                <span className="text-lg sm:text-xl font-bold hidden sm:inline tracking-tight">{settings.site_name}</span>
              </Link>
              <div className="flex items-center gap-1.5 sm:gap-3">
                <ThemeToggle />
                <Button variant="ghost" size="sm" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="hidden md:inline-flex text-sm">
                  Features
                </Button>
                <Button variant="ghost" size="sm" onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="hidden md:inline-flex text-sm">
                  Pricing
                </Button>
                <Button variant="ghost" size="sm" onClick={() => document.getElementById('gmc')?.scrollIntoView({ behavior: 'smooth' })} className="hidden lg:inline-flex text-sm">
                  GMC Scanner
                </Button>
                {isAuthenticated ? (
                  <Button size="sm" onClick={() => navigate('/dashboard')} className="shadow-sm">
                    Dashboard <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                ) : (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/auth')} className="hidden sm:inline-flex">
                      Sign In
                    </Button>
                    <Button size="sm" onClick={() => navigate('/auth')} className="shadow-sm">
                      Get Started
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Layered background effects */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[700px] bg-primary/5 rounded-full blur-[140px] -z-10 animate-pulse-slow" />
          <div className="absolute -top-32 -left-32 w-96 h-96 bg-info/8 rounded-full blur-[100px] -z-10 animate-float" />
          <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-primary/6 rounded-full blur-[120px] -z-10" />
          <div className="absolute top-1/2 right-0 w-72 h-72 bg-warning/5 rounded-full blur-[80px] -z-10 animate-float" style={{ animationDelay: '2s' }} />
          <div className="absolute inset-0 -z-10 opacity-[0.02]" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }} />

          <div className="container mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-40">
            <div className="text-center max-w-5xl mx-auto">
              <div className="animate-slide-up">
                <Badge variant="secondary" className="px-4 py-1.5 text-xs sm:text-sm font-medium mb-6 sm:mb-8 bg-primary/10 text-primary border border-primary/30 backdrop-blur-sm">
                  <Star className="h-3.5 w-3.5 mr-1.5 fill-warning text-warning" />
                  Trusted by 500+ e-commerce merchants
                </Badge>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-extrabold tracking-tight leading-[1.08] mb-6 sm:mb-8 animate-slide-up-delay-1">
                Your All-in-One{' '}
                <span className="text-gradient">E-commerce</span>
                <br className="hidden sm:block" />
                {' '}Product Toolkit
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10 sm:mb-12 animate-slide-up-delay-2 px-4">
                Scrape products from Shopify & WooCommerce, fix them with AI, scan for GMC compliance, and push directly to your store — all from one powerful dashboard.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-slide-up-delay-3 px-4">
                <Button size="lg" onClick={handleGetStarted} className="text-base sm:text-lg px-8 sm:px-10 py-6 shadow-glow gap-2.5 rounded-xl group">
                  <Sparkles className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                  Get Started
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="text-base sm:text-lg px-8 sm:px-10 py-6 gap-2.5 rounded-xl group">
                  <Play className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  See How It Works
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 mt-12 sm:mt-16 animate-slide-up-delay-3 text-muted-foreground/60">
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Shield className="h-4 w-4" />
                  <span>Secure & Encrypted</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Clock className="h-4 w-4" />
                  <span>Setup in 2 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Zap className="h-4 w-4" />
                  <span>No credit card required</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="border-y bg-muted/20">
          <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {stats.map((stat, i) => (
                <div key={i} className="text-center group">
                  <div className="inline-flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-primary/10 mb-3 group-hover:bg-primary/15 group-hover:scale-110 transition-all duration-300">
                    <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  </div>
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground tracking-tight">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground mt-1 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="container mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-36">
          <div className="text-center mb-14 sm:mb-20">
            <Badge variant="secondary" className="mb-4 px-4 py-1.5 text-xs sm:text-sm border-primary/20">
              <Zap className="h-3.5 w-3.5 mr-1.5 text-warning fill-warning" /> Powerful Features
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 sm:mb-5 tracking-tight">
              Everything You Need to <span className="text-gradient">Manage Products</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed">
              From scraping to AI fixing to GMC compliance — a complete toolkit for modern e-commerce
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group relative overflow-hidden border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-lg hover:-translate-y-1 bg-card/60 backdrop-blur-sm"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <CardHeader className="pb-3 relative z-10">
                  <div className="p-3 bg-primary/10 rounded-xl w-fit mb-3 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                    <feature.icon className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-base sm:text-lg font-bold">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <CardDescription className="text-sm leading-relaxed">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-muted/15 border-y">
          <div className="container mx-auto px-4 sm:px-6 py-20 sm:py-28">
            <div className="text-center mb-14 sm:mb-20">
              <Badge variant="secondary" className="mb-4 px-4 py-1.5 text-xs sm:text-sm border-primary/20">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Simple Workflow
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 tracking-tight">
                Three Steps to <span className="text-gradient">Success</span>
              </h2>
              <p className="text-muted-foreground text-sm sm:text-lg max-w-xl mx-auto">
                Get your products scraped, fixed, and published in minutes
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 max-w-5xl mx-auto">
              {[
                { step: '01', title: 'Scrape Products', description: 'Paste any Shopify or WooCommerce URL. We extract all products, variants, images, and metadata automatically.', icon: Globe, color: 'from-blue-500 to-cyan-500' },
                { step: '02', title: 'Fix with AI', description: 'AI fixes titles, descriptions, SEO handles, tags, availability, and inventory — or scan for GMC compliance issues.', icon: Bot, color: 'from-violet-500 to-purple-500' },
                { step: '03', title: 'Export or Push', description: 'Download as CSV/XML, push directly to your store, or download a GMC compliance PDF report.', icon: Rocket, color: 'from-amber-500 to-orange-500' },
              ].map((item, i) => (
                <div key={i} className="text-center group">
                  <div className="relative inline-flex items-center justify-center w-18 h-18 sm:w-20 sm:h-20 rounded-3xl gradient-primary shadow-glow mb-6 group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="h-7 w-7 sm:h-8 sm:w-8 text-primary-foreground" />
                    <span className="absolute -top-2.5 -right-2.5 bg-background text-foreground text-xs font-bold w-7 h-7 rounded-full border-2 border-primary flex items-center justify-center shadow-sm">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2.5">{item.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xs mx-auto">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* GMC Scanner Section */}
        <section id="gmc" className="container mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-36">
          <div className="text-center mb-14 sm:mb-20">
            <Badge variant="secondary" className="mb-4 px-4 py-1.5 text-xs sm:text-sm border-primary/20">
              <Search className="h-3.5 w-3.5 mr-1.5" /> Pro Feature
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 sm:mb-5 tracking-tight">
              Fix GMC Suspensions in <span className="text-gradient">60 Seconds</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Scan any Shopify or WooCommerce store. Get a detailed compliance report with copy-paste fix instructions for every issue.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto mb-16">
            {gmcCategories.map((cat, i) => (
              <Card key={i} className="text-center group hover:border-primary/30 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border/50 bg-card/60">
                <CardHeader className="pb-2">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${cat.color} mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                    <cat.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-base sm:text-lg font-bold">{cat.label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed">{cat.desc}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {[
                { step: '1', title: 'Enter Store URL', desc: 'Paste any Shopify, WooCommerce, or e-commerce store URL and select the GMC suspension category.', color: 'bg-rose-500/10 text-rose-500' },
                { step: '2', title: 'We Scan & Analyze', desc: 'Our crawler visits your homepage, policy pages, and products — running 30+ compliance checks plus AI analysis.', color: 'bg-amber-500/10 text-amber-500' },
                { step: '3', title: 'Fix Every Issue', desc: 'Get a scored report with exact fix instructions, copy-paste templates, and download as PDF.', color: 'bg-emerald-500/10 text-emerald-500' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${item.color} font-bold text-lg mb-3`}>
                    {item.step}
                  </div>
                  <h4 className="font-bold mb-1.5 text-sm sm:text-base">{item.title}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="bg-muted/15 border-y">
          <div className="container mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-36">
            <div className="text-center mb-14 sm:mb-20">
              <Badge variant="secondary" className="mb-4 px-4 py-1.5 text-xs sm:text-sm border-primary/20">
                <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Pricing
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 tracking-tight">
                Simple, <span className="text-gradient">Transparent</span> Pricing
              </h2>
              <p className="text-muted-foreground text-sm sm:text-lg max-w-2xl mx-auto">
                Choose the plan that fits your business. No hidden fees, cancel anytime.
              </p>
            </div>

            {loadingPlans ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">Pricing plans coming soon.</p>
              </div>
            ) : (
              <div className={`grid gap-5 sm:gap-6 max-w-5xl mx-auto ${
                plans.length === 1 ? 'grid-cols-1 max-w-md' :
                plans.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-3xl' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }`}>
                {plans.map((plan) => {
                  const tierKey = getTierKey(plan.name);
                  const tier = TIER_DETAILS[tierKey];
                  const isPopular = tierKey === 'standard';
                  const TierIcon = tier?.icon || Package;

                  return (
                    <Card
                      key={plan.id}
                      className={`relative flex flex-col transition-all duration-300 hover:-translate-y-2 bg-card/80 backdrop-blur-sm ${
                        isPopular
                          ? 'border-2 border-primary shadow-glow lg:scale-[1.03]'
                          : 'border-border/50 hover:border-primary/30 hover:shadow-lg'
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                          <Badge className="gradient-primary text-primary-foreground px-5 py-1 text-xs sm:text-sm font-semibold shadow-glow">
                            Most Popular
                          </Badge>
                        </div>
                      )}
                      <CardHeader className={`text-center pb-2 ${isPopular ? 'pt-8' : ''}`}>
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${tier?.gradient || 'from-primary/10 to-primary/10'} mx-auto mb-3`}>
                          <TierIcon className="h-6 w-6 text-primary" />
                        </div>
                        <CardTitle className="text-lg sm:text-xl font-bold">{plan.name}</CardTitle>
                        {(tier?.tagline || plan.description) && (
                          <CardDescription className="text-xs sm:text-sm mt-1">{tier?.tagline || plan.description}</CardDescription>
                        )}
                        <div className="pt-5">
                          <span className="text-4xl sm:text-5xl font-extrabold tracking-tight">${plan.price_usd}</span>
                          <span className="text-muted-foreground ml-1 text-base sm:text-lg">
                            {formatInterval(plan.interval_days)}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col pt-4">
                        <ul className="space-y-2.5 flex-1 mb-6">
                          {(tier?.detailedFeatures || []).map((feat, fIdx) => {
                            const isHeader = feat.endsWith(':');
                            return (
                              <li key={fIdx} className={`flex items-start gap-2.5 ${isHeader ? 'pt-1' : ''}`}>
                                {isHeader ? (
                                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{feat}</span>
                                ) : (
                                  <>
                                    <div className="mt-0.5 p-0.5 rounded-full bg-success/15 shrink-0">
                                      <Check className="h-3.5 w-3.5 text-success" />
                                    </div>
                                    <span className="text-sm text-foreground/80">{feat}</span>
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>

                        {tierKey === 'basic' && (
                          <div className="border-t pt-3 mb-4">
                            <p className="text-xs text-muted-foreground mb-2 font-medium">Not included:</p>
                            <ul className="space-y-1.5">
                              {['AI Product Fixer', 'Push to Store', 'GMC Scanner'].map((f, i) => (
                                <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground/60">
                                  <X className="h-3 w-3" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {tierKey === 'standard' && (
                          <div className="border-t pt-3 mb-4">
                            <p className="text-xs text-muted-foreground mb-2 font-medium">Upgrade to Pro for:</p>
                            <ul className="space-y-1.5">
                              {['GMC Compliance Scanner', 'AI GMC Fixer', 'AI Privacy Policy Fixer', 'PDF Reports'].map((f, i) => (
                                <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground/60">
                                  <X className="h-3 w-3" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <Button
                          onClick={handleGetStarted}
                          className={`w-full text-sm sm:text-base py-5 rounded-xl ${isPopular ? 'shadow-glow' : ''}`}
                          variant={isPopular ? 'default' : 'outline'}
                        >
                          {tierKey === 'pro' ? 'Go Pro' : 'Get Started'}
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background">
          <div className="container mx-auto px-4 sm:px-6 py-20 sm:py-28">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12 sm:mb-16">
                <Badge variant="secondary" className="mb-4 px-4 py-1.5 text-xs sm:text-sm border-primary/20">FAQ</Badge>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 tracking-tight">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm sm:text-lg">
                  Everything you need to know about Wopfy
                </p>
              </div>
              <Accordion type="single" collapsible className="w-full space-y-2">
                {[
                  { q: 'How does product scraping work?', a: 'Paste a Shopify or WooCommerce collection URL, and Wopfy automatically extracts all product info including titles, descriptions, prices, images, variants, and metadata.' },
                  { q: 'What does the AI Product Fixer do?', a: 'It scans all product fields and fixes titles, descriptions, SEO handles, tags, product types, availability (sets out-of-stock items to in-stock), and inventory quantities — all powered by AI.' },
                  { q: 'What is the GMC Scanner?', a: 'It scans your entire store for Google Merchant Center compliance issues across 4 categories: Misrepresentation, Policy Violations, Website Quality, and Product Data. You get a scored report with exact fix instructions.' },
                  { q: 'Can I fix products directly from the dashboard?', a: 'Yes! Select products in the table and use AI Fix to repair all fields without exporting. You can also fix CSV files before downloading in the GMC Scanner.' },
                  { q: 'What export formats are supported?', a: 'Shopify CSV, WooCommerce CSV, Google Merchant XML, and GMC compliance PDF reports.' },
                  { q: "What's the difference between plans?", a: 'Basic ($29/mo) covers Shopify scraping and exports. Standard ($49/mo) adds WooCommerce, AI Fix, store pushing, and CSV tools. Pro ($79/mo) includes everything plus GMC Scanner, AI GMC Fixer, and the AI Privacy Policy Fixer.' },
                  { q: 'Which AI providers can I use?', a: 'Bring your own API key from Google Gemini, Groq, OpenRouter, OpenAI or Anthropic. Use free tiers (Gemini, Groq) or paid plans — the app automatically falls back to another saved provider if one is rate-limited or out of quota.' },
                  { q: 'What is the AI Privacy Policy Fixer?', a: 'Available on the Pro plan. Paste your existing Privacy Policy, Terms, Refund or Shipping Policy and the AI rewrites it into a Google Merchant Center–compliant HTML version you can preview, copy, or download.' },
                  { q: 'Can I cancel anytime?', a: 'Yes, cancel your subscription anytime. Access remains active until the end of your current billing period. All features are disabled after expiry until you renew.' },
                ].map((item, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border rounded-xl px-4 sm:px-6 bg-card/40 backdrop-blur-sm">
                    <AccordionTrigger className="text-sm sm:text-base font-semibold hover:no-underline py-4 sm:py-5">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pb-4 sm:pb-5">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="relative overflow-hidden rounded-3xl gradient-primary p-8 sm:p-14 lg:p-20 text-center shadow-glow">
            <div className="absolute top-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/3 rounded-full blur-[120px]" />
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold mb-4 sm:mb-6 text-primary-foreground tracking-tight">
                Ready to Supercharge Your Store?
              </h2>
              <p className="text-primary-foreground/80 text-sm sm:text-lg mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
                Join hundreds of merchants managing products more efficiently with Wopfy. Scrape, fix with AI, pass GMC compliance, and push to your store.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={handleGetStarted}
                  className="text-base sm:text-lg px-10 sm:px-12 py-6 sm:py-7 rounded-xl font-semibold gap-2 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <Sparkles className="h-5 w-5" />
                  Get Started Today
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t bg-card/20 backdrop-blur-sm mt-auto">
          <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-14">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
              <div className="sm:col-span-2 lg:col-span-1">
                <div className="flex items-center gap-2.5 mb-4">
                  {settings.site_logo_url ? (
                    <img src={settings.site_logo_url} alt={settings.site_name} className="h-7 w-auto object-contain" />
                  ) : (
                    <div className="p-1.5 gradient-primary rounded-lg">
                      <Rocket className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                  <span className="font-bold tracking-tight">{settings.site_name}</span>
                </div>
                <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                  The all-in-one product management and GMC compliance tool for e-commerce merchants.
                </p>
              </div>

              <div>
                <h4 className="font-bold text-sm mb-4">Quick Links</h4>
                <ul className="space-y-2.5">
                  <li><button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</button></li>
                  <li><button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</button></li>
                  <li><button onClick={() => document.getElementById('gmc')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm text-muted-foreground hover:text-foreground transition-colors">GMC Scanner</button></li>
                  <li><Link to="/auth" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Sign In</Link></li>
                  <li><Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact Us</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-sm mb-4">Legal</h4>
                <ul className="space-y-2.5">
                  {policyLinks.map((link) => (
                    <li key={link.path}>
                      <Link to={link.path} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="border-t mt-10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                © {new Date().getFullYear()} {settings.site_name}. All rights reserved.
              </p>
              <p className="text-xs text-muted-foreground">
                Built for Shopify & WooCommerce merchants
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Home;
